import { PrimaryButton } from '@/common/Button';
import TrackWrapper from '@/common/TrackWrapper';
import { RemainingTasks } from '@/home/OrderStatusCards/OrderPlacedCard/VerificationTasks';
import { defaultImageHandler } from '@/home/utils/defaultImageHandler';
import formatEvent from '@/home/utils/formatEvent';
import { type BffFeedItem } from '~/contracts/home';
import { useHandleNativePurchaseOnClick } from '~/native/home/hooks';
import { getNativeVdpPayload, nativeMyCarsPayload } from '~/native/home/utils';
import { StatusCardType } from './feedItemMapper';
import {
  BlueDot,
  buttonStyles,
  Card,
  cardWrapperStyles,
  DetailsContentWrapper,
  DetailsWrapper,
  Image,
  ImageContainer,
  SubTitle,
  Title,
  TitleWrapper,
  TrimMileage,
  YMM,
  YMMTMWrapper
} from './StatusCard.styles';
import {
  getCustomAttributesDescription,
  getSubText,
  getTitleText
} from './utils';

type OwnProps = {
  cardStatus: StatusCardType;
  feedItem: BffFeedItem;
};

export default function OrderStatusCard({ cardStatus, feedItem }: OwnProps) {
  const handleNativePurchaseOnClick = useHandleNativePurchaseOnClick();

  if (
    feedItem.content == null ||
    feedItem.content.vehicle == null ||
    feedItem.callToAction == null ||
    (cardStatus === StatusCardType.Order &&
      feedItem.content.openVerifications == null)
  ) {
    return null;
  }

  const {
    callToAction: { title: callToActionTitle, link: callToActionLink },
    content: {
      vehicle: {
        make,
        model,
        imageUrl,
        id: vehicleId,
        trim,
        year,
        vehicleInventoryType,
        stockNumber,
        mileage = 0
      },
      openVerifications
    }
  } = feedItem;

  const customAttributes = {
    description: getCustomAttributesDescription(cardStatus),
    vehicleId
  };

  const title = getTitleText(
    cardStatus,
    make,
    openVerifications?.hasOpenVerifications ?? false
  );
  const subText = getSubText(
    cardStatus,
    openVerifications?.hasOpenVerifications ?? false,
    feedItem.type
  );

  const shouldSendNativePayload =
    cardStatus === StatusCardType.PurchaseInProgress ||
    cardStatus === StatusCardType.Order;

  const shouldShowRemainingTasks =
    cardStatus === StatusCardType.Order &&
    (openVerifications?.hasOpenVerifications ?? false);

  function getNativePayload() {
    switch (cardStatus) {
      case StatusCardType.PurchaseInProgress:
      case StatusCardType.Order:
        return '';
      case StatusCardType.Purchased:
        return nativeMyCarsPayload;
      case StatusCardType.Reserved:
        return getNativeVdpPayload(vehicleId);
    }
  }

  const nativePayload = {
    make,
    model,
    trim,
    year,
    vehicleId,
    vehicleImage: imageUrl,
    inventoryType: vehicleInventoryType,
    stockNumber
  };

  return (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', customAttributes)}
      className={cardWrapperStyles}
    >
      <Card>
        <TitleWrapper>
          <Title className=" text-blue-6 tl-header-m m:tl-header-xl">
            {title}
          </Title>
          <SubTitle className="text-grey-5 tl-body-m m:tl-body-l">
            {subText}
          </SubTitle>
        </TitleWrapper>
        {shouldShowRemainingTasks && (
          <a
            href={callToActionLink}
            data-qa="Status Card Btn CTA"
            data-analytics-id="Home feed card"
            data-analytics-attributes={JSON.stringify(customAttributes)}
            data-native-included="false"
            onClick={event => handleNativePurchaseOnClick(event, nativePayload)}
            className="block m:hidden"
          >
            <RemainingTasks
              cta={callToActionTitle}
              hasOpenVerifications={
                openVerifications?.hasOpenVerifications ?? false
              }
              openVerificationsCount={
                openVerifications?.openVerificationsCount ?? 0
              }
            />
          </a>
        )}
        <DetailsWrapper>
          <DetailsContentWrapper>
            <YMMTMWrapper>
              <YMM>
                {year} {make} {model}
              </YMM>
              <TrimMileage>
                {trim}{' '}
                {mileage ? (
                  <>
                    <BlueDot /> {mileage.toLocaleString()} miles
                  </>
                ) : (
                  ''
                )}
              </TrimMileage>
            </YMMTMWrapper>
            {shouldShowRemainingTasks ? (
              <a
                href={callToActionLink}
                data-qa="order-placed-verifications-button"
                data-analytics-id="Home feed card"
                data-analytics-attributes={JSON.stringify(customAttributes)}
                data-native-included="false"
                onClick={event =>
                  handleNativePurchaseOnClick(event, nativePayload)
                }
                className="hidden max-w-[291px] m:block"
              >
                <RemainingTasks
                  cta={callToActionTitle}
                  hasOpenVerifications={
                    openVerifications?.hasOpenVerifications ?? false
                  }
                  openVerificationsCount={
                    openVerifications?.openVerificationsCount ?? 0
                  }
                />
              </a>
            ) : (
              <a
                href={callToActionLink}
                data-analytics-id="Home feed card"
                data-qa="Status Card Btn CTA"
                data-analytics-attributes={JSON.stringify(customAttributes)}
                data-native-included={!shouldSendNativePayload}
                data-native-payload={getNativePayload()}
                onClick={event => {
                  if (shouldSendNativePayload) {
                    handleNativePurchaseOnClick(event, nativePayload);
                  }
                }}
              >
                <PrimaryButton className={buttonStyles}>
                  {callToActionTitle}
                </PrimaryButton>
              </a>
            )}
          </DetailsContentWrapper>
          <ImageContainer>
            <a
              href={callToActionLink}
              data-qa="Status Card Image CTA"
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify(customAttributes)}
              data-native-included={!shouldSendNativePayload}
              data-native-payload={getNativePayload()}
              onClick={event => {
                if (shouldSendNativePayload) {
                  handleNativePurchaseOnClick(event, nativePayload);
                }
              }}
              className="flex"
            >
              <Image
                alt={`${make} ${model}`}
                src={imageUrl}
                onError={defaultImageHandler}
              />
            </a>
          </ImageContainer>
        </DetailsWrapper>
      </Card>
    </TrackWrapper>
  );
}
